// ExchangeCreate.jsx
import React, {useEffect, useMemo, useState} from "react";
import {Alert, App, Button, Checkbox, Form, Input, InputNumber, Modal, Select, Spin, Typography,} from "antd";
import {PlusOutlined, SwapOutlined} from "@ant-design/icons";
import {useNavigate} from "react-router-dom";
import {useDispatch} from "react-redux";

import api from "../../../api/axiosClient";
import {
  floor2,
  formatMoney,
  normalizeCurrencyMethods,
  normalizePairs,
  normalizePayouts,
  toNumber,
} from "../../../components/exchange/v1/utils/exhcangeHelpers";
import Shell from "../../../components/exchange/v1/ui/Shell";

import {upsertFromApi} from "../../../store/exchangeActiveSlice";
import {useWs} from "../../../components/ws/WsProvider";

const {Title, Text} = Typography;

const API = {
  currencyMethods: "/exh/currency-methods/",
  pairs: "/exh/pairs/",
  payouts: "/exh/user-payments/",
  transactions: "/exh/order-transactions/",
};

const STAGES = {
  CREATE: 0,
};

function ExchangeStageCreate({
                               stage,
                               loading,

                               // data
                               currencyMethods,
                               pairs,
                               payouts,

                               // state
                               fromCmId,
                               setFromCmId,
                               toCmId,
                               setToCmId,
                               amountSend,
                               setAmountSend,
                               comment,
                               setComment,
                               acceptedTerms,
                               setAcceptedTerms,
                               accepted2h,
                               setAccepted2h,
                               selectedPayoutId,
                               setSelectedPayoutId,

                               // modal add payout
                               addOpen,
                               openAddModal,
                               closeAddModal,
                               addForm,
                               saveNewPayout,

                               // computed
                               fromOptions,
                               toOptions,
                               currentPair,
                               rateText,
                               amountReceive,

                               // actions
                               onSwap,
                               canCreate,
                               onCreate,
                               userPayMethodOptions,
                               eligiblePayouts,
                             }) {
  return (
    <Shell
      stage={stage}
      title="Создание заявки на обмен"
      subtitle="Этап 1. Выберите направление, сумму и реквизит получения."
      minBodyHeight={760}
    >
      <div className="mt-4 mb-4">
        <Alert
          type="warning"
          showIcon
          className="uppercase font-bold"
          message="Важно!"
          description="После начала обмена у вас будет 2 часа на выполнение всех переводов и подтверждений."
        />
      </div>
      <Spin spinning={loading}>
        <div className="flex columns-2">
          <div className="w-[100%]">
            <div className="font-ubuntu font-bold text-[28px] leading-[38px] text-[var(--blue-2)]">
              Отдаёте
            </div>
          </div>
          <div className="w-[100%]">
            <div className="font-ubuntu font-bold text-[28px] leading-[38px] text-[var(--blue-2)]">
              Получаете
            </div>
          </div>
        </div>

        <div className="flex items-end gap-3 mt-4">
          <div className="w-[100%]">
            <Select
              size="large"
              className="w-full"
              loading={loading}
              value={fromCmId}
              onChange={setFromCmId}
              options={fromOptions}
              placeholder="Выберите валюту и метод (FROM)"
            />
          </div>

          <button
            type="button"
            onClick={onSwap}
            className="z-[1] w-12 h-12 bg-transparent border-0 cursor-pointer flex items-center justify-center"
            title="Поменять местами"
            disabled={!fromCmId || !toCmId}
          >
            <SwapOutlined style={{fontSize: 20, color: "#0B2EA6"}}/>
          </button>

          <div className="w-[100%]">
            <Select
              size="large"
              className="w-full"
              loading={loading}
              value={toCmId}
              onChange={setToCmId}
              options={toOptions}
              placeholder="Выберите валюту и метод (TO)"
              disabled={!fromCmId || toOptions.length === 0}
            />
          </div>
        </div>

        <div className="flex columns-2 gap-10 mt-10">
          <div className="w-[100%]">
            <div className="text-[16px] font-extrabold text-[#0B1B3A] mb-2">
              Сумма отдачи
            </div>
            <InputNumber
              size="large"
              className="w-full"
              min={0}
              step={0.01}
              value={amountSend}
              onChange={(v) => setAmountSend(floor2(v))}
              placeholder="0.00"
              disabled={!currentPair}
            />
          </div>

          <div className="w-[100%]">
            <div className="text-[16px] font-extrabold text-[#0B1B3A] mb-2">
              Сумма получения (расчёт)
            </div>
            <Input
              size="large"
              readOnly
              value={
                currentPair
                  ? `${formatMoney(amountReceive)} ${currentPair.currency_to}`
                  : "—"
              }
            />
          </div>
        </div>

        <div className="mt-5">
          <span className="block text-[15px] font-medium text-[var(--blue-3)]">
            {rateText}
          </span>
          <span className="mt-1 block text-[13px] text-[var(--grey-3)]">
            актуально на: {new Date().toISOString().replace("T", " ").slice(0, 19)}{" "}
            UTC
          </span>
        </div>

        <div className="my-6 h-px bg-black/10"/>

        <Title className="!m-0 !text-[30px] !font-extrabold !text-[#0B1B3A]">
          Реквизиты для получения
        </Title>
        <Text className="block mt-1 text-[15px] text-[#6B7280]">
          Показаны только реквизиты, которые совпадают с выбранным способом
          получения (TO: валюта + метод).
        </Text>

        <div className="mt-6 bg-[#F3F6FF] rounded-[16px] p-6 max-[520px]:p-4">
          <div className="text-[24px] font-extrabold text-[#0B2EA6]">
            Ваши реквизиты
          </div>

          <div className="mt-5">
            {eligiblePayouts.length > 0 ? (
              <Select
                size="large"
                className="w-full"
                value={selectedPayoutId}
                onChange={setSelectedPayoutId}
                options={eligiblePayouts.map((p) => ({
                  value: p.id,
                  label: `${p.type} · ${p.detail}`,
                }))}
                placeholder="Выберите реквизит"
              />
            ) : (
              <div className="flex items-center justify-between gap-3">
                <Text className="text-[#6B7280]">
                  Нет подходящих реквизитов для выбранного получения.
                </Text>
                <Button
                  type="primary"
                  icon={<PlusOutlined/>}
                  onClick={openAddModal}
                  disabled={!toCmId}
                >
                  Добавить реквизит
                </Button>
              </div>
            )}
          </div>
        </div>

        <div className="mt-5">
          <div className="text-[16px] font-extrabold text-[#0B1B3A] mb-2">
            Комментарий к заявке (необязательно)
          </div>
          <Input.TextArea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Введите дополнительную информацию..."
            autoSize={{minRows: 4, maxRows: 6}}
          />
        </div>

        <div className="mt-4 space-y-2">
          <Checkbox
            checked={acceptedTerms}
            onChange={(e) => setAcceptedTerms(e.target.checked)}
          >
            Ознакомлен и согласен с{" "}
            <a href="/private" className="font-extrabold text-[#0B2EA6] underline">
              условиями обмена
            </a>
          </Checkbox>

          <Checkbox
            checked={accepted2h}
            onChange={(e) => setAccepted2h(e.target.checked)}
          >
            Я понимаю, что после начала обмена у меня есть 2 часа на завершение
            переводов
          </Checkbox>
        </div>

        <Button
          type="primary"
          size="large"
          disabled={!canCreate}
          loading={loading}
          onClick={onCreate}
          className="mt-4 w-full !h-[54px] !rounded-[12px] !font-extrabold !bg-[#0B63F6] shadow-[0_16px_34px_rgba(11,99,246,0.22)] disabled:!bg-[#9BBDFB] disabled:shadow-none"
        >
          Начать обмен
        </Button>

        {/* Modal: add payout */}
        <Modal
          title="Добавить реквизит"
          open={addOpen}
          onCancel={closeAddModal}
          onOk={saveNewPayout}
          okText="Сохранить"
          cancelText="Отмена"
          destroyOnHidden
        >
          <Form layout="vertical" form={addForm}>
            <Form.Item
              label="Тип реквизита"
              name="type"
              rules={[{required: true, message: "Выберите тип"}]}
            >
              <Select
                options={[
                  {value: "CARD", label: "CARD"},
                  {value: "PHONE", label: "PHONE"},
                ]}
              />
            </Form.Item>

            <Form.Item
              label="Валюта и метод"
              name="pay_method_id"
              rules={[{required: true, message: "Выберите валюту и метод"}]}
            >
              <Select options={userPayMethodOptions} placeholder="Например: USD · Zelle"/>
            </Form.Item>

            <Form.Item
              label="Значение реквизита"
              name="detail"
              rules={[
                {required: true, message: "Введите реквизит"},
                {min: 3, message: "Слишком коротко"},
              ]}
            >
              <Input placeholder="Например: +1 555..., 5168 **** ..."/>
            </Form.Item>
          </Form>
        </Modal>
      </Spin>
    </Shell>
  );
}

export default function ExchangeCreate() {
  const {message} = App.useApp();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {sendRequest} = useWs();

  const [loading, setLoading] = useState(true);
  const [currencyMethods, setCurrencyMethods] = useState([]);
  const [pairs, setPairs] = useState([]);
  const [payouts, setPayouts] = useState([]);

  useEffect(() => {
    document.title = "USDSeller | Новый обмен";
  }, []);

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setLoading(true);

        const [cmRes, pairsRes, payoutsRes] = await Promise.all([
          api.get(API.currencyMethods),
          api.get(API.pairs),
          api.get(API.payouts),
        ]);

        const cm = normalizeCurrencyMethods(cmRes.data);
        const exPairs = normalizePairs(pairsRes.data, cm);
        const userPayouts = normalizePayouts(payoutsRes.data, cm);

        if (!alive) return;
        setCurrencyMethods(cm);
        setPairs(exPairs);
        setPayouts(userPayouts);
      } catch (e) {
        if (!alive) return;
        console.error(e);
        message.error("Не удалось загрузить данные для обмена");
      } finally {
        if (!alive) return;
        setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [message]);

  const [fromCmId, setFromCmId] = useState(null);
  const [toCmId, setToCmId] = useState(null);
  const [amountSend, setAmountSend] = useState(100);
  const [comment, setComment] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [accepted2h, setAccepted2h] = useState(false);

  // payout selection
  const [selectedPayoutId, setSelectedPayoutId] = useState(null);

  // modal add payout
  const [addOpen, setAddOpen] = useState(false);
  const [addForm] = Form.useForm();

  const cmById = useMemo(() => {
    const m = new Map();
    (currencyMethods || []).forEach((x) => m.set(x.id, x));
    return m;
  }, [currencyMethods]);

  const cmLabel = (cm) =>
    `${cm.currency} · ${cm.method?.title ?? cm.method?.code ?? "—"}`;

  const fromOptions = useMemo(() => {
    const activePairs = (pairs || []).filter((p) => p.is_active);
    const uniq = new Map();

    activePairs.forEach((p) => {
      const key = `${p.currency_from}|${p.method_from?.id}`;
      if (!uniq.has(key)) {
        const found = (currencyMethods || []).find(
          (cm) =>
            cm.currency === p.currency_from && cm.method?.id === p.method_from?.id
        );
        if (found) uniq.set(key, found);
      }
    });

    return Array.from(uniq.values()).map((cm) => ({
      value: cm.id,
      label: cmLabel(cm),
    }));
  }, [pairs, currencyMethods]);

  const toOptions = useMemo(() => {
    if (!fromCmId) return [];
    const fromCm = cmById.get(fromCmId);
    if (!fromCm) return [];

    const activePairs = (pairs || []).filter((p) => p.is_active);
    const available = activePairs.filter(
      (p) =>
        p.currency_from === fromCm.currency &&
        p.method_from?.id === fromCm.method?.id
    );

    const uniq = new Map();
    available.forEach((p) => {
      const key = `${p.currency_to}|${p.method_to?.id}`;
      if (!uniq.has(key)) {
        const found = (currencyMethods || []).find(
          (cm) =>
            cm.currency === p.currency_to && cm.method?.id === p.method_to?.id
        );
        if (found) uniq.set(key, found);
      }
    });

    return Array.from(uniq.values()).map((cm) => ({
      value: cm.id,
      label: cmLabel(cm),
    }));
  }, [fromCmId, pairs, currencyMethods, cmById]);

  useEffect(() => {
    if (!fromCmId && fromOptions.length) setFromCmId(fromOptions[0].value);
  }, [fromOptions, fromCmId]);

  useEffect(() => {
    if (!fromCmId) {
      setToCmId(null);
      return;
    }
    if (!toOptions.length) {
      setToCmId(null);
      return;
    }
    if (!toCmId || !toOptions.some((x) => x.value === toCmId)) {
      setToCmId(toOptions[0].value);
    }
  }, [fromCmId, toOptions, toCmId]);

  const currentPair = useMemo(() => {
    if (!fromCmId || !toCmId) return null;

    const fromCm = cmById.get(String(fromCmId));
    const toCm = cmById.get(String(toCmId));
    if (!fromCm || !toCm) return null;

    const fromMethodId = fromCm?.method?.id ? String(fromCm.method.id) : null;
    const toMethodId = toCm?.method?.id ? String(toCm.method.id) : null;

    return (pairs || []).find((p) => {
      if (!p?.is_active) return false;
      return (
        p.currency_from === fromCm.currency &&
        String(p.method_from?.id || "") === String(fromMethodId || "") &&
        p.currency_to === toCm.currency &&
        String(p.method_to?.id || "") === String(toMethodId || "")
      );
    });
  }, [fromCmId, toCmId, pairs, cmById]);

  const rate = useMemo(() => {
    if (!currentPair) return 0;

    const buy = toNumber(currentPair.buy_rate);
    const sell = toNumber(currentPair.sell_rate);
    const ratio = toNumber(currentPair.ratio_rate || 1);


    const buyEff = ratio > 0 ? buy / ratio : 0;
    const sellEff = sell * ratio;

    if (currentPair.currency_from === "USD" && currentPair.currency_to === "RUB") {
      return buyEff; // множитель: RUB за 1 USD
    }

    if (currentPair.currency_from === "RUB" && currentPair.currency_to === "USD") {
      return sellEff > 0 ? 1 / sellEff : 0;
    }

    // fallback
    return buyEff;
  }, [currentPair]);

  const amountReceive = useMemo(() => {
    if (!currentPair) return 0;

    const send = toNumber(amountSend);
    const buy = toNumber(currentPair.buy_rate);
    const sell = toNumber(currentPair.sell_rate);
    const ratio = toNumber(currentPair.ratio_rate || 1);

    const buyEff = ratio > 0 ? buy / ratio : 0;
    const sellEff = sell * ratio;

    if (currentPair.currency_from === "USD" && currentPair.currency_to === "RUB") {
      return floor2(send * buyEff);
    }

    if (currentPair.currency_from === "RUB" && currentPair.currency_to === "USD") {
      return sellEff > 0 ? floor2(send / sellEff) : 0;
    }

    return floor2(send * toNumber(rate));
  }, [amountSend, rate, currentPair]);

  const rateText = useMemo(() => {
    if (!currentPair) return "Курс: —";

    const buy = toNumber(currentPair.buy_rate);
    const sell = toNumber(currentPair.sell_rate);
    const ratio = toNumber(currentPair.ratio_rate || 1);


    const buyEff = ratio > 0 ? buy / ratio : 0;
    const sellEff = sell * ratio;

    if (currentPair.currency_from === "USD" && currentPair.currency_to === "RUB") {
      return `Курс: покупка 1 USD = ${buyEff.toFixed(4)} RUB`;
    }

    if (currentPair.currency_from === "RUB" && currentPair.currency_to === "USD") {
      const usdPerRub = sellEff > 0 ? 1 / sellEff : 0;
      return `Курс: продажа 1 RUB = ${usdPerRub.toFixed(8)} USD`;
    }

    return `Курс: 1 ${currentPair.currency_from} = ${rate.toFixed(4)} ${currentPair.currency_to}`;
  }, [currentPair, rate]);

  const eligiblePayouts = useMemo(() => {
    if (!toCmId) return [];
    const toCm = cmById.get(toCmId);
    if (!toCm) return [];

    return (payouts || []).filter(
      (p) =>
        p?.pay_method?.currency === toCm.currency &&
        p?.pay_method?.method?.id === toCm.method?.id
    );
  }, [payouts, toCmId, cmById]);

  useEffect(() => {
    if (!eligiblePayouts.length) {
      setSelectedPayoutId(null);
      return;
    }
    if (
      !selectedPayoutId ||
      !eligiblePayouts.some((p) => p.id === selectedPayoutId)
    ) {
      setSelectedPayoutId(eligiblePayouts[0].id);
    }
  }, [eligiblePayouts, selectedPayoutId]);

  const userPayMethodOptions = useMemo(() => {
    return (currencyMethods || [])
      .filter((cm) => cm.is_active && cm.method)
      .map((cm) => ({value: cm.id, label: cmLabel(cm)}));
  }, [currencyMethods]);

  const openAddModal = () => {
    addForm.setFieldsValue({
      type: "CARD",
      pay_method_id: toCmId ?? null,
      detail: "",
    });
    setAddOpen(true);
  };

  const closeAddModal = () => setAddOpen(false);

  const saveNewPayout = async () => {
    try {
      const values = await addForm.validateFields();

      const payload = {
        type: values.type,
        detail: String(values.detail || "").trim(),
        pay_method_id: String(values.pay_method_id),
      };

      const res = await api.post(API.payouts, payload);

      const created = normalizePayouts([res.data], currencyMethods)[0] || null;
      if (!created) throw new Error("bad_create_response");

      setPayouts((prev) => [created, ...(prev || [])]);
      message.success("Реквизит добавлен");
      setAddOpen(false);

      if (
        toCmId &&
        created?.pay_method?.id &&
        String(created.pay_method.id) === String(toCmId)
      ) {
        setSelectedPayoutId(created.id);
      }
    } catch (e) {
      if (e?.response?.data) {
        message.error("Не удалось сохранить реквизит");
        console.error("create payout error:", e.response.data);
      }
    }
  };

  const onSwap = () => {
    const a = fromCmId;
    const b = toCmId;
    setFromCmId(b);
    setToCmId(a);
  };

  const canCreate = useMemo(() => {
    return (
      !!currentPair &&
      toNumber(amountSend) > 0 &&
      !!selectedPayoutId &&
      acceptedTerms &&
      accepted2h
    );
  }, [currentPair, amountSend, selectedPayoutId, acceptedTerms, accepted2h]);

  const onCreate = async () => {
    if (!canCreate) return;

    setLoading(true);
    try {
      const payload = {
        pair_id: String(currentPair.id),
        payout_id: selectedPayoutId ? String(selectedPayoutId) : null,
        amount_from: floor2(amountSend),
      };

      const wsResponse = await sendRequest("transaction.create", payload);
      const createdTx = wsResponse?.data;
      const createdId = createdTx?.id;

      message.success("Заявка создана");

      if (!createdId) {
        message.error("Не удалось открыть заявку: отсутствует id в ответе");
        return;
      }

      dispatch(upsertFromApi([createdTx]));
      navigate(`/exchange/flow/${createdId}`, {state: {tx: createdTx}});
    } catch (e) {
      // WS error response: { type: "response", status: 4xx, errors: {...} }
      const errors = e?.errors;
      if (errors) {
        const firstField = Object.keys(errors)[0];
        const firstMsg = Array.isArray(errors[firstField])
          ? errors[firstField][0]
          : typeof errors[firstField] === "object"
            ? errors[firstField]?.detail ?? String(errors[firstField])
            : String(errors[firstField]);

        message.error(firstMsg || "Не удалось создать заявку");
        console.error("create transaction error:", errors);
        return;
      }

      message.error(e?.message || "Не удалось создать заявку");
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ExchangeStageCreate
      stage={STAGES.CREATE}
      loading={loading}
      currencyMethods={currencyMethods}
      pairs={pairs}
      payouts={payouts}
      fromCmId={fromCmId}
      setFromCmId={setFromCmId}
      toCmId={toCmId}
      setToCmId={setToCmId}
      amountSend={amountSend}
      setAmountSend={setAmountSend}
      comment={comment}
      setComment={setComment}
      acceptedTerms={acceptedTerms}
      setAcceptedTerms={setAcceptedTerms}
      accepted2h={accepted2h}
      setAccepted2h={setAccepted2h}
      selectedPayoutId={selectedPayoutId}
      setSelectedPayoutId={setSelectedPayoutId}
      addOpen={addOpen}
      openAddModal={openAddModal}
      closeAddModal={closeAddModal}
      addForm={addForm}
      saveNewPayout={saveNewPayout}
      fromOptions={fromOptions}
      toOptions={toOptions}
      currentPair={currentPair}
      rateText={rateText}
      amountReceive={amountReceive}
      onSwap={onSwap}
      canCreate={canCreate}
      onCreate={onCreate}
      userPayMethodOptions={userPayMethodOptions}
      eligiblePayouts={eligiblePayouts}
    />
  );
}